import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Trash2, Send, Mail, Phone, Download, Plus } from "lucide-react";
import { api } from "@/lib/api";

const ST = { neu: "Neu", angebot_gesendet: "Angebot gesendet", storniert: "Abgelehnt" };
const emptyItem = () => ({ description: "", qty: 1, unit_price: "" });

export default function InquiriesView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quoteItems, setQuoteItems] = useState({}); // { [inquiryId]: [{description, qty, unit_price}] }
  const [notes, setNotes] = useState({});
  const [sending, setSending] = useState(null);

  const load = useCallback(async () => {
    try { const r = await api.get("/inquiries"); setItems(r.data); }
    catch (e) { toast.error("Konnte Anfragen nicht laden."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rowsFor = (i) => quoteItems[i.id] || (i.quote_items?.length ? i.quote_items.map((x) => ({ ...x })) : [emptyItem()]);

  const addRow = (i) => setQuoteItems((p) => ({ ...p, [i.id]: [...rowsFor(i), emptyItem()] }));
  const removeRow = (i, idx) => setQuoteItems((p) => {
    const rows = rowsFor(i).filter((_, k) => k !== idx);
    return { ...p, [i.id]: rows.length ? rows : [emptyItem()] };
  });
  const updateRow = (i, idx, field, value) => setQuoteItems((p) => {
    const rows = rowsFor(i).map((r, k) => (k === idx ? { ...r, [field]: value } : r));
    return { ...p, [i.id]: rows };
  });

  const total = (rows) => rows.reduce((s, r) => s + (Number(r.qty) || 0) * (Number(r.unit_price) || 0), 0);

  const sendQuote = async (i) => {
    const rows = rowsFor(i)
      .filter((r) => r.description.trim() && Number(r.unit_price) > 0)
      .map((r) => ({ description: r.description.trim(), qty: Number(r.qty) || 1, unit_price: Number(r.unit_price) }));
    if (rows.length === 0) { toast.error("Bitte mindestens eine Position mit Bezeichnung und Preis eintragen."); return; }
    setSending(i.id);
    try {
      const { data } = await api.post(`/inquiries/${i.id}/quote`, { items: rows, note: notes[i.id] || "", base_url: window.location.origin });
      setItems((p) => p.map((x) => (x.id === i.id ? data : x)));
      toast.success(`Angebot über ${data.quote_total.toFixed(2)} € an ${i.email} gesendet.`);
    } catch (e) { toast.error("Angebot fehlgeschlagen."); }
    finally { setSending(null); }
  };

  const remove = async (i) => {
    try { await api.delete(`/inquiries/${i.id}`); setItems((p) => p.filter((x) => x.id !== i.id)); toast.success("Anfrage gelöscht."); }
    catch (e) { toast.error("Löschen fehlgeschlagen."); }
  };

  const downloadQuote = async (i) => {
    const win = window.open("", "_blank");
    try {
      const res = await api.get(`/inquiries/${i.id}/quote/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      if (win) win.location.href = url; else window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      if (win) win.close();
      toast.error("Angebot-PDF konnte nicht erstellt werden.");
    }
  };

  return (
    <div data-testid="inquiries-view">
      <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mb-8">Anfragen</h1>
      {loading ? <p className="text-[#888] font-mono text-sm py-20 text-center">Lädt…</p>
        : items.length === 0 ? <div data-testid="inq-empty" className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-16 text-center text-[#888]">Noch keine Anfragen.</div>
        : (
          <div className="space-y-3" data-testid="inquiries-list">
            {items.map((i) => {
              const rows = rowsFor(i);
              return (
              <div key={i.id} data-testid={`inquiry-${i.id}`} className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-4 sm:p-6">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
                  <div className="lg:col-span-6">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${i.status === "neu" ? "bg-white text-[#050505]" : "bg-green-500/20 text-green-300 border border-green-500/30"}`}>{ST[i.status] || i.status}</span>
                      <span className="font-mono text-xs text-white/90 bg-white/10 px-2 py-1 rounded-full">{i.reference}</span>
                      {i.quote_total > 0 && <span data-testid={`quote-total-${i.id}`} className="font-mono text-xs text-[#aaa]">Angebot: {i.quote_total.toFixed(2)} €</span>}
                    </div>
                    <p className="text-lg mt-2">{i.name}</p>
                    <div className="flex flex-col gap-1 mt-1 text-sm text-[#999]">
                      <a href={`mailto:${i.email}`} className="inline-flex items-center gap-2 hover:text-white"><Mail size={14} />{i.email}</a>
                      {i.phone && <a href={`tel:${i.phone}`} className="inline-flex items-center gap-2 hover:text-white"><Phone size={14} />{i.phone}</a>}
                    </div>
                  </div>
                  <div className="lg:col-span-6">
                    <p className="text-sm text-white">{i.topic}{i.device ? ` · ${i.device}` : ""}</p>
                    <p className="text-sm text-[#888] mt-1 leading-relaxed">{i.message}</p>
                  </div>
                </div>

                <div className="mt-5 pt-5 border-t border-white/10">
                  <p className="font-mono text-xs uppercase tracking-widest text-[#888] mb-3">Angebot erstellen · mehrere Positionen möglich</p>
                  <div className="space-y-2">
                    {rows.map((r, idx) => (
                      <div key={idx} className="flex flex-wrap gap-2 items-center" data-testid={`quote-row-${i.id}-${idx}`}>
                        <input data-testid={`quote-desc-${i.id}-${idx}`} placeholder="Position, z.B. Displaytausch" value={r.description}
                          onChange={(e) => updateRow(i, idx, "description", e.target.value)}
                          className="w-full sm:flex-1 sm:w-auto bg-[#050505] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-[#666] focus:border-white/40 focus:outline-none" />
                        <input data-testid={`quote-qty-${i.id}-${idx}`} type="number" min="1" step="1" placeholder="Menge" value={r.qty}
                          onChange={(e) => updateRow(i, idx, "qty", e.target.value)}
                          className="w-[30%] sm:w-20 bg-[#050505] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-[#666] focus:border-white/40 focus:outline-none" />
                        <input data-testid={`quote-price-${i.id}-${idx}`} type="number" min="0" step="0.01" placeholder="Preis €" value={r.unit_price}
                          onChange={(e) => updateRow(i, idx, "unit_price", e.target.value)}
                          className="w-[38%] sm:w-28 bg-[#050505] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-[#666] focus:border-white/40 focus:outline-none" />
                        <button onClick={() => removeRow(i, idx)} data-testid={`quote-row-remove-${i.id}-${idx}`} className="w-9 h-9 shrink-0 rounded-lg border border-white/10 flex items-center justify-center text-[#888] hover:text-red-400 hover:border-red-400/40 transition-colors ml-auto sm:ml-0"><Trash2 size={14} /></button>
                      </div>
                    ))}
                  </div>
                  <button onClick={() => addRow(i)} data-testid={`quote-add-row-${i.id}`} className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#999] hover:text-white transition-colors">
                    <Plus size={13} /> Position hinzufügen
                  </button>

                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    <input data-testid={`quote-note-${i.id}`} placeholder="Notiz für den Kunden (optional)" value={notes[i.id] || ""} onChange={(e) => setNotes((p) => ({ ...p, [i.id]: e.target.value }))}
                      className="flex-1 min-w-[200px] bg-[#050505] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-[#666] focus:border-white/40 focus:outline-none" />
                    <span className="font-mono text-sm text-white shrink-0">Summe: {total(rows).toFixed(2)} €</span>
                  </div>

                  <div className="mt-4 flex gap-2">
                    <button onClick={() => sendQuote(i)} disabled={sending === i.id} data-testid={`send-quote-${i.id}`} className="inline-flex items-center justify-center gap-2 text-sm rounded-full bg-white text-[#050505] px-5 py-2.5 hover:bg-[#d1d5db] transition-colors disabled:opacity-50"><Send size={14} /> {sending === i.id ? "Sendet…" : "Angebot per E-Mail senden"}</button>
                    <button onClick={() => downloadQuote(i)} disabled={!i.quote_items?.length} data-testid={`download-quote-${i.id}`} title={i.quote_items?.length ? "Angebot als PDF herunterladen" : "Erst Angebot senden"} className="inline-flex items-center justify-center gap-2 text-sm rounded-full border border-white/15 text-white px-5 py-2.5 hover:bg-white/10 transition-colors disabled:opacity-40"><Download size={14} /> PDF</button>
                    <button onClick={() => remove(i)} data-testid={`inq-delete-${i.id}`} className="w-9 h-9 rounded-lg border border-white/10 flex items-center justify-center text-[#888] hover:text-red-400 hover:border-red-400/40 transition-colors shrink-0 ml-auto"><Trash2 size={15} /></button>
                  </div>
                </div>
              </div>
            );})}
          </div>
        )}
    </div>
  );
}
